<script setup>
// SharkFoto Image Studio（nuxt#19 P3）。复刻 design studio.js 的共享画布编辑器：
// 上传 → canvas 实时渲染 → 下载。一个组件覆盖 7 个工具，按 mode 切换控制面板与绘制逻辑。
// mode: watermark | border | corners | meme | bw | adjust | grayscale
//   - grayscale 复用 studio.js 的 bw 渲染（style 固定 grayscale），但面板只显示说明（无风格切换）。
// 纯浏览器内运算（Canvas / FileReader / URL.createObjectURL），不上传、不依赖任何 npm 包。
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useAuthModals } from "@/composables/useAuthModals";
import { usePaywallModal } from "@/composables/usePaywallModal";
import { useBatch } from "@/composables/useBatch.js";
import { useCanvasDecode } from "@/composables/useCanvasDecode";
import { tierOf, fileTooLargeMsg, looksLikeEditableImage } from "@/settings/free-tools/batch-limits.js";

const props = defineProps({
  mode: { type: String, default: "watermark" },
  slug: { type: String, default: "" },
});
const { downloadBlob } = useFreeToolsDownload();
const { decodeForCanvas, decodeErrorMsg } = useCanvasDecode();
const store = useStore();

/* ---- 配置（每个 mode 的文案 / 下载名 / 动作按钮）---- */
const META = {
  watermark: { dl: "watermark", another: "Watermark another", ext: "png", type: "image/png" },
  border: { dl: "border", another: "Frame another", ext: "png", type: "image/png" },
  corners: { dl: "corners", another: "Round another", ext: "png", type: "image/png" },
  meme: { dl: "meme", another: "New meme", ext: "png", type: "image/png" },
  bw: { dl: "bw", another: "Convert another", ext: "png", type: "image/png" },
  adjust: { dl: "adjust", another: "Adjust another", ext: "png", type: "image/png" },
  grayscale: { dl: "grayscale", another: "Convert another", ext: "png", type: "image/png" },
};
const meta = computed(() => META[props.mode] || META.watermark);
// grayscale 在画布层面就是 bw（style=grayscale）
const drawMode = computed(() => (props.mode === "grayscale" ? "bw" : props.mode));
const isWatermark = computed(() => props.mode === "watermark");
const isBorder = computed(() => props.mode === "border");
const isCorners = computed(() => props.mode === "corners");
const isMeme = computed(() => props.mode === "meme");
const isBw = computed(() => props.mode === "bw");
const isAdjust = computed(() => props.mode === "adjust");
const isGrayscale = computed(() => props.mode === "grayscale");

/* ---- 控制状态（对应各 HTML 面板的输入）---- */
const wmText = ref("© SharkFoto");
const wmSize = ref(5); // %
const wmOpacity = ref(45); // %
const wmPos = ref("br");
const POSITIONS = ["tl", "tc", "tr", "cl", "center", "cr", "bl", "bc", "br"];

const bWidth = ref(24); // px
const borderColor = ref("#ffffff");
const BORDER_SWATCHES = ["#ffffff", "#000000", "#8b5cf6", "#ff6f9c", "#fbbf24", "#34d399"];

const radius = ref(12); // %

const topText = ref("WHEN THE CODE");
const bottomText = ref("WORKS FIRST TRY");

const bwStyle = ref("grayscale"); // grayscale | sepia | contrast

const bright = ref(100); // %
const contrast = ref(100); // %
const saturate = ref(100); // %

/* ---- 上传 / 画布 ---- */
const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileInput = ref(null);
const canvas = ref(null);
const fileName = ref("image.png");
const dimReadout = ref("—");
const thumbUrl = ref("");
const fxNudge = ref(false);
let img = null;
let baseName = "image";
let loaded = false;
let lastThumbUrl = "";

function revokeThumb() {
  if (lastThumbUrl) {
    URL.revokeObjectURL(lastThumbUrl);
    lastThumbUrl = "";
  }
}

async function loadFile(file) {
  if (!file || !looksLikeEditableImage(file)) {
    alert("Please choose an image file.");
    return;
  }
  const _m = fileTooLargeMsg(file, "image");
  if (_m) { alert(_m); return; }
  // 浏览器解不了的源(heic/tiff/dds…)→ 先后端转 png(用户无感);命名仍用原始 file.name。
  let _f;
  try { _f = await decodeForCanvas(file); }
  catch (e) { alert(decodeErrorMsg(file)); return; }
  baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  fileName.value = file.name;
  revokeThumb();
  const url = URL.createObjectURL(_f);
  lastThumbUrl = url;
  const next = new Image();
  next.onload = async () => {
    img = next;
    loaded = true;
    thumbUrl.value = url;
    stage.value = "work";
    fxNudge.value = false;
    await nextTick();
    render();
  };
  next.onerror = () => alert("Could not read that image.");
  next.src = url;
}

function pick() {
  fileInput.value && fileInput.value.click();
}
function onInput(e) {
  handleFiles(e.target.files);
  e.target.value = ""; // 允许 add more / 重选同名文件
}
function onDrop(e) {
  e.preventDefault();
  dragging.value = false;
  handleFiles(e.dataTransfer && e.dataTransfer.files);
}
function reset() {
  stage.value = "drop";
  loaded = false;
  img = null;
  fxNudge.value = false;
  if (fileInput.value) fileInput.value.value = "";
}

/* ---- 绘制（逐位复刻 studio.js render）---- */
function roundRect(c, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}
function memeText(ctx, txt, cx, y, fs, maxw) {
  if (!txt) return;
  ctx.font = `700 ${fs}px Impact, "Anton", "Arial Black", sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = fs * 0.14;
  ctx.fillStyle = "#fff";
  ctx.strokeText(txt, cx, y, maxw * 0.95);
  ctx.fillText(txt, cx, y, maxw * 0.95);
}

// 把当前共享控件的设置画到任意 canvas c（用任意 image），供单文件实时预览 render() 与批量
// convertFileBatch() 共用。所有 mode 的绘制逻辑都在此，一份设置应用到所有文件。
function drawTo(c, image) {
  const ctx = c.getContext("2d");
  const w = image.naturalWidth,
    h = image.naturalHeight;
  const m = drawMode.value;

  if (m === "border") {
    const bw = +bWidth.value,
      col = borderColor.value;
    c.width = w + bw * 2;
    c.height = h + bw * 2;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = col;
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(image, bw, bw, w, h);
  } else if (m === "corners") {
    const pct = +radius.value,
      r = (Math.min(w, h) * pct) / 100;
    c.width = w;
    c.height = h;
    ctx.clearRect(0, 0, w, h);
    ctx.save();
    roundRect(ctx, 0, 0, w, h, r);
    ctx.clip();
    ctx.drawImage(image, 0, 0, w, h);
    ctx.restore();
  } else if (m === "meme") {
    c.width = w;
    c.height = h;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(image, 0, 0, w, h);
    const fs = Math.max(20, w * 0.09);
    memeText(ctx, (topText.value || "").toUpperCase(), w / 2, fs * 1.05, fs, w);
    memeText(ctx, (bottomText.value || "").toUpperCase(), w / 2, h - fs * 0.4, fs, w);
  } else if (m === "bw") {
    c.width = w;
    c.height = h;
    ctx.clearRect(0, 0, w, h);
    const style = isGrayscale.value ? "grayscale" : bwStyle.value;
    ctx.filter = style === "sepia" ? "sepia(1)" : style === "contrast" ? "grayscale(1) contrast(1.4)" : "grayscale(1)";
    ctx.drawImage(image, 0, 0, w, h);
    ctx.filter = "none";
  } else if (m === "adjust") {
    c.width = w;
    c.height = h;
    ctx.clearRect(0, 0, w, h);
    ctx.filter = `brightness(${+bright.value}%) contrast(${+contrast.value}%) saturate(${+saturate.value}%)`;
    ctx.drawImage(image, 0, 0, w, h);
    ctx.filter = "none";
  } else {
    // watermark
    c.width = w;
    c.height = h;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(image, 0, 0, w, h);
    const txt = wmText.value || "",
      op = +wmOpacity.value / 100,
      sz = +wmSize.value / 100;
    if (txt) {
      const fs = Math.max(14, Math.min(w, h) * sz);
      ctx.font = `700 ${fs}px Public Sans, system-ui, sans-serif`;
      ctx.fillStyle = `rgba(255,255,255,${op})`;
      ctx.strokeStyle = `rgba(0,0,0,${op * 0.5})`;
      ctx.lineWidth = fs * 0.06;
      ctx.textBaseline = "middle";
      const pos = wmPos.value,
        pad = fs * 0.6,
        tw = ctx.measureText(txt).width;
      let x, y;
      ctx.textAlign = "left";
      if (pos === "center") {
        ctx.textAlign = "center";
        x = w / 2;
        y = h / 2;
      } else {
        const right = pos[1] === "r";
        const bottom = pos[0] === "b";
        x = right ? w - pad - tw : pad;
        y = bottom ? h - pad - fs / 2 : pad + fs / 2;
      }
      ctx.strokeText(txt, x, y);
      ctx.fillText(txt, x, y);
    }
  }
}

function render() {
  if (!loaded) return;
  const c = canvas.value;
  if (!c) return;
  drawTo(c, img);
  dimReadout.value = c.width + " × " + c.height + " px";
}

/* ---------- 批量模式(nuxt#21):多文件 → 队列。纯客户端(canvas)，meme 模式不批量 ---------- */
// meme 需逐图改文字，没法「一套设置应用所有文件」，故不参与批量(多文件退回单文件第一张)。
const batchable = computed(() => props.mode !== "meme");
const batchMode = ref(false);
const tierB = computed(() => tierOf(!!store.state.user.token, store.state.user.userInfo));
const tokenB = computed(() => store.state.user.token || "");
const userIdB = computed(() => (store.state.user.userInfo && store.state.user.userInfo.user_info_id) || "");
const _auth = useAuthModals();
const _paywall = usePaywallModal();
const batch = useBatch({
  category: "image",
  client: true, // 纯前端(canvas)，零服务器成本
  tier: tierB,
  token: tokenB,
  userId: userIdB,
  clientConvert: (file) => convertFileBatch(file),
  buildQuery: () => "",
  onSignup: () => _auth.openSignInModal(),
  onUpgrade: () => _paywall.openPaywall(["DST", "PES", "EXP", "JEF", "VP3", "HUS", "XXX", "EMD"]),
});

// 把任意 file 载入 Image → drawTo(共享设置) → toBlob。镜像单文件 download() 的产物(png, q=0.95)，
// 但输入是任意 file（不依赖模块级 img / loaded）。一套共享控件设置应用到所有文件。
async function convertFileBatch(file) {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error("Could not read image"));
      im.src = url;
    });
    const c = document.createElement("canvas");
    drawTo(c, image); // 复用单文件渲染逻辑（当前 mode + 共享参数）
    const blob = await new Promise((r) => c.toBlob(r, meta.value.type, 0.95));
    if (!blob) throw new Error("Conversion failed");
    return { blob, ext: meta.value.ext };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function handleFiles(files) {
  if (!files || !files.length) return;
  if (batchMode.value) { batch.addFiles(files); return; }     // 已在批量 → add more
  if (batchable.value && files.length > 1) { enterBatch(files); return; } // 多文件 → 进批量
  loadFile(files[0]);                                          // 单文件 / meme 多文件 → 原单文件流(取第一张)
}
function enterBatch(files) {
  batchMode.value = true;
  batch.clearAll();
  batch.addFiles(files);
}
function addMoreFiles() { if (fileInput.value) fileInput.value.click(); }
// 套餐变化(登录/升级后) → 重算锁定；队列清空 → 退出批量回到 dropzone。
watch([tierB], () => batch.reevaluate());
watch(() => batch.items.value.length, (n) => {
  if (batchMode.value && n === 0) batchMode.value = false;
});

// 任意控制变化 → 实时重绘
watch([wmText, wmSize, wmOpacity, wmPos, bWidth, borderColor, radius, topText, bottomText, bwStyle, bright, contrast, saturate, () => props.mode], () => {
  if (loaded) render();
});

function download() {
  const c = canvas.value;
  if (!loaded || !c) return;
  c.toBlob(
    (b) => { if (!b) return; downloadBlob(b, baseName + "-" + meta.value.dl + "." + meta.value.ext); },
    meta.value.type,
    0.95
  );
  fxNudge.value = true;
}

onBeforeUnmount(revokeThumb);
</script>

<template>
  <div class="studio">
    <input ref="fileInput" type="file" accept="image/*" :multiple="batchable" hidden @change="onInput" />

    <!-- 拖放区 -->
    <div
      v-show="stage === 'drop' && !batchMode"
      class="dz"
      :class="{ drag: dragging }"
      @click="pick"
      @dragenter.prevent="dragging = true"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop="onDrop"
    >
      <div class="dz-ico">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4M7 9l5-5 5 5" /><path d="M5 16v3a1 1 0 001 1h12a1 1 0 001-1v-3" /></svg>
      </div>
      <h3>Drop your image here</h3>
      <div class="hint">or click to browse — PNG, JPG, WEBP, GIF</div>
      <div class="formats"><span>PNG</span><span>JPG</span><span>WEBP</span><span>GIF</span></div>
      <div class="dz-or">Processed right in your browser — never stored</div>
      <button class="btn btn-primary btn-lg" type="button" @click.stop="pick">Choose Image</button>
    </div>

    <!-- batch 多文件队列（nuxt#21）：拖入 >1 个文件时进入；共享参数走 #params 插槽（meme 不批量） -->
    <div v-if="batchMode" class="ws">
      <BatchQueue :batch="batch" category="image" :client="true" :file-count-max="20" @add-more="addMoreFiles">
        <template #params>
          <!-- watermark -->
          <template v-if="isWatermark">
            <div class="ctl">
              <label>Watermark text</label>
              <input v-model="wmText" type="text" class="field" />
            </div>
            <div class="ctl">
              <label>Size <span class="v">{{ wmSize }}%</span></label>
              <input v-model.number="wmSize" type="range" min="2" max="15" />
            </div>
            <div class="ctl">
              <label>Opacity <span class="v">{{ wmOpacity }}%</span></label>
              <input v-model.number="wmOpacity" type="range" min="10" max="100" />
            </div>
            <div class="ctl">
              <label>Position</label>
              <div class="pos-grid">
                <button v-for="p in POSITIONS" :key="p" type="button" class="pos" :class="{ active: wmPos === p }" @click="wmPos = p" />
              </div>
            </div>
          </template>

          <!-- border -->
          <template v-else-if="isBorder">
            <div class="ctl">
              <label>Border width <span class="v">{{ bWidth }}px</span></label>
              <input v-model.number="bWidth" type="range" min="2" max="120" />
            </div>
            <div class="ctl">
              <label>Border color</label>
              <div class="sw-row">
                <button v-for="s in BORDER_SWATCHES" :key="s" type="button" class="sw" :class="{ active: borderColor === s }" :style="{ background: s }" @click="borderColor = s" />
              </div>
            </div>
          </template>

          <!-- corners -->
          <template v-else-if="isCorners">
            <div class="ctl">
              <label>Corner radius <span class="v">{{ radius }}%</span></label>
              <input v-model.number="radius" type="range" min="0" max="50" />
            </div>
          </template>

          <!-- bw (grayscale / sepia / contrast) -->
          <template v-else-if="isBw">
            <div class="ctl">
              <label>Style</label>
              <div class="seg">
                <button type="button" class="seg-btn" :class="{ active: bwStyle === 'grayscale' }" @click="bwStyle = 'grayscale'">Grayscale</button>
                <button type="button" class="seg-btn" :class="{ active: bwStyle === 'sepia' }" @click="bwStyle = 'sepia'">Sepia</button>
                <button type="button" class="seg-btn" :class="{ active: bwStyle === 'contrast' }" @click="bwStyle = 'contrast'">High contrast</button>
              </div>
            </div>
          </template>

          <!-- adjust (brightness / contrast / saturation) -->
          <template v-else-if="isAdjust">
            <div class="ctl">
              <label>Brightness <span class="v">{{ bright }}%</span></label>
              <input v-model.number="bright" type="range" min="0" max="200" />
            </div>
            <div class="ctl">
              <label>Contrast <span class="v">{{ contrast }}%</span></label>
              <input v-model.number="contrast" type="range" min="0" max="200" />
            </div>
            <div class="ctl">
              <label>Saturation <span class="v">{{ saturate }}%</span></label>
              <input v-model.number="saturate" type="range" min="0" max="200" />
            </div>
          </template>

          <div style="font-size: 12.5px; color: var(--tx-faint); font-weight: 600">One set of settings applies to all files.</div>
        </template>
      </BatchQueue>
    </div>

    <!-- 工作区 -->
    <div v-if="stage === 'work' && !batchMode" class="ws">
      <div class="ws-grid">
        <div class="ws-preview" :class="{ checker: isCorners }"><canvas ref="canvas" class="ws-canvas" /></div>

        <div class="ws-panel">
          <div class="ws-file">
            <img class="thumb" :src="thumbUrl" alt="" />
            <div class="meta">
              <div class="fn">{{ fileName }}</div>
              <div class="fsz">{{ dimReadout }}</div>
            </div>
            <button class="rm" type="button" title="Remove" @click="reset">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
            </button>
          </div>

          <!-- watermark -->
          <template v-if="isWatermark">
            <div class="ctl">
              <label>Watermark text</label>
              <input v-model="wmText" type="text" class="field" />
            </div>
            <div class="ctl">
              <label>Size <span class="v">{{ wmSize }}%</span></label>
              <input v-model.number="wmSize" type="range" min="2" max="15" />
            </div>
            <div class="ctl">
              <label>Opacity <span class="v">{{ wmOpacity }}%</span></label>
              <input v-model.number="wmOpacity" type="range" min="10" max="100" />
            </div>
            <div class="ctl">
              <label>Position</label>
              <div class="pos-grid">
                <button v-for="p in POSITIONS" :key="p" type="button" class="pos" :class="{ active: wmPos === p }" @click="wmPos = p" />
              </div>
            </div>
          </template>

          <!-- border -->
          <template v-else-if="isBorder">
            <div class="ctl">
              <label>Border width <span class="v">{{ bWidth }}px</span></label>
              <input v-model.number="bWidth" type="range" min="2" max="120" />
            </div>
            <div class="ctl">
              <label>Border color</label>
              <div class="sw-row">
                <button v-for="s in BORDER_SWATCHES" :key="s" type="button" class="sw" :class="{ active: borderColor === s }" :style="{ background: s }" @click="borderColor = s" />
              </div>
            </div>
          </template>

          <!-- corners -->
          <template v-else-if="isCorners">
            <div class="ctl">
              <label>Corner radius <span class="v">{{ radius }}%</span></label>
              <input v-model.number="radius" type="range" min="0" max="50" />
            </div>
            <p class="note">Output is a PNG so the rounded-off corners stay transparent. Push the slider to max on a square image for a circle.</p>
          </template>

          <!-- meme -->
          <template v-else-if="isMeme">
            <div class="ctl">
              <label>Top text</label>
              <input v-model="topText" type="text" class="field" />
            </div>
            <div class="ctl">
              <label>Bottom text</label>
              <input v-model="bottomText" type="text" class="field" />
            </div>
          </template>

          <!-- bw (grayscale / sepia / contrast) -->
          <template v-else-if="isBw">
            <div class="ctl">
              <label>Style</label>
              <div class="seg">
                <button type="button" class="seg-btn" :class="{ active: bwStyle === 'grayscale' }" @click="bwStyle = 'grayscale'">Grayscale</button>
                <button type="button" class="seg-btn" :class="{ active: bwStyle === 'sepia' }" @click="bwStyle = 'sepia'">Sepia</button>
                <button type="button" class="seg-btn" :class="{ active: bwStyle === 'contrast' }" @click="bwStyle = 'contrast'">High contrast</button>
              </div>
            </div>
          </template>

          <!-- grayscale (pure, no style switch) -->
          <template v-else-if="isGrayscale">
            <p class="note">Your image is converted to pure grayscale (black &amp; white) using accurate luminance weighting, with a live preview.</p>
          </template>

          <!-- adjust (brightness / contrast / saturation) -->
          <template v-else-if="isAdjust">
            <div class="ctl">
              <label>Brightness <span class="v">{{ bright }}%</span></label>
              <input v-model.number="bright" type="range" min="0" max="200" />
            </div>
            <div class="ctl">
              <label>Contrast <span class="v">{{ contrast }}%</span></label>
              <input v-model.number="contrast" type="range" min="0" max="200" />
            </div>
            <div class="ctl">
              <label>Saturation <span class="v">{{ saturate }}%</span></label>
              <input v-model.number="saturate" type="range" min="0" max="200" />
            </div>
          </template>

          <!-- 引流卡片：下载后出现 -->
          <div v-if="fxNudge" class="fx-nudge">
            <span class="ni"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 4.4L18 9l-4.2 1.6L12 15l-1.8-4.4L6 9l4.2-1.6z" /></svg></span>
            <span class="nt"><b>Now make it go viral</b><span>Turn this image into an AI Effect — one tap</span></span>
            <NuxtLink class="ngo" to="/#effects">Try effect</NuxtLink>
          </div>

          <div class="ws-actions">
            <button class="btn btn-primary btn-lg" type="button" @click="download">Apply &amp; Download</button>
            <button class="btn btn-outline" type="button" @click="reset">{{ meta.another }}</button>
          </div>

          <p class="gen-note">Runs right in your browser — never stored.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.studio {
  width: 100%;
}

/* dropzone */
.dz {
  border: 2px dashed var(--line-strong);
  border-radius: 20px;
  background: var(--surface);
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: 0.18s;
}
.dz.drag,
.dz:hover {
  border-color: #f97316;
  background: color-mix(in oklab, #f97316 6%, var(--surface));
}
.dz-ico {
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  color: #f97316;
}
.dz-ico svg {
  width: 100%;
  height: 100%;
}
.dz h3 {
  font-family: var(--font-h);
  font-size: 20px;
  font-weight: 700;
}
.dz .hint {
  color: var(--tx-dim);
  font-size: 14px;
  margin-top: 6px;
}
.dz .formats {
  margin: 12px 0;
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}
.dz .formats span {
  font-size: 12px;
  font-weight: 700;
  color: var(--tx-faint);
  border: 1px solid var(--line);
  border-radius: 7px;
  padding: 3px 10px;
}
.dz-or {
  color: var(--tx-faint);
  font-size: 13px;
  margin: 10px 0 16px;
}

/* workspace */
.ws-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 22px;
  align-items: start;
  border: 1px solid var(--line);
  border-radius: 22px;
  background: var(--surface);
  overflow: hidden;
}
.ws-preview {
  display: grid;
  place-items: center;
  padding: 24px;
  background: var(--surface-2);
  min-height: 320px;
  min-width: 0;
}
.ws-preview.checker {
  background-image: conic-gradient(#2a2d3a 25%, transparent 0 50%, #2a2d3a 0 75%, transparent 0);
  background-size: 22px 22px;
  background-color: #1c1e27;
}
.ws-canvas {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 8px 30px -10px rgba(0, 0, 0, 0.5);
}
.ws-panel {
  padding: 24px;
  border-left: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* file row */
.ws-file {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 10px 12px;
  background: var(--surface-2);
}
.ws-file .thumb {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  flex: none;
  background: var(--surface);
}
.ws-file .meta {
  flex: 1;
  min-width: 0;
}
.ws-file .fn {
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ws-file .fsz {
  font-size: 12px;
  color: var(--tx-faint);
}
.ws-file .rm {
  background: none;
  border: none;
  color: var(--tx-faint);
  cursor: pointer;
  flex: none;
  padding: 4px;
}
.ws-file .rm:hover {
  color: var(--tx);
}

/* controls */
.ctl label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--tx-faint);
  display: block;
  margin-bottom: 9px;
}
.ctl label .v {
  color: #f4cd76;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
}
.ctl .field {
  width: 100%;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: 11px;
  padding: 11px 13px;
  color: var(--tx);
  font-family: var(--font-b);
  font-size: 15px;
  outline: none;
}
.ctl .field:focus {
  border-color: #f97316;
}
.ctl input[type="range"] {
  width: 100%;
  accent-color: #f97316;
}
.note {
  font-size: 12.5px;
  color: var(--tx-faint);
  line-height: 1.5;
}

/* watermark position grid */
.pos-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  max-width: 132px;
}
.pos {
  aspect-ratio: 1;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: var(--surface-2);
  cursor: pointer;
  padding: 0;
}
.pos.active {
  border-color: #f97316;
  background: color-mix(in oklab, #f97316 22%, var(--surface-2));
  box-shadow: 0 0 0 1px #f97316 inset;
}

/* color swatches */
.sw-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.sw {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 2px solid var(--line);
  cursor: pointer;
  padding: 0;
}
.sw.active {
  border-color: var(--tx);
  box-shadow: 0 0 0 2px var(--surface), 0 0 0 4px #f97316;
}

/* segmented (bw style) */
.seg {
  display: flex;
  gap: 6px;
  border: 1px solid var(--line);
  border-radius: 11px;
  padding: 4px;
  background: var(--surface-2);
}
.seg-btn {
  flex: 1;
  border: none;
  background: none;
  color: var(--tx-dim);
  font-family: var(--font-b);
  font-size: 13px;
  font-weight: 600;
  padding: 8px 6px;
  border-radius: 8px;
  cursor: pointer;
}
.seg-btn.active {
  background: #f97316;
  color: #0b0d13;
}

/* fx nudge */
.fx-nudge {
  display: flex;
  align-items: center;
  gap: 11px;
  background: color-mix(in oklab, #f97316 8%, var(--surface));
  border: 1px solid color-mix(in oklab, #f97316 26%, var(--line));
  border-radius: 12px;
  padding: 11px 13px;
}
.fx-nudge .ni {
  width: 24px;
  height: 24px;
  color: #f97316;
  flex: none;
}
.fx-nudge .ni svg {
  width: 100%;
  height: 100%;
}
.fx-nudge .nt {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.fx-nudge .nt b {
  font-size: 13px;
}
.fx-nudge .nt span {
  font-size: 12px;
  color: var(--tx-dim);
}
.fx-nudge .ngo {
  flex: none;
  font-size: 13px;
  font-weight: 700;
  color: #f97316;
  text-decoration: none;
}

.ws-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}
.gen-note {
  font-size: 12.5px;
  color: var(--tx-faint);
}

@media (max-width: 760px) {
  .ws-grid {
    grid-template-columns: 1fr;
  }
  .ws-panel {
    border-left: none;
    border-top: 1px solid var(--line);
  }
}
</style>
